import { t } from 'i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import { withMask } from 'use-mask-input'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { Button, InputField } from '@/components/ui'
import { ComboboxField } from '@/components/ui/combobox'
import type { CompradorPessoaFisicaStepSchemaInput } from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pf.schema'
import { useClientePorCpfProposta } from '@/features/proposta/brasil/hooks/use-cliente-por-cpf-proposta'
import { useDadosMaePai } from '@/features/proposta/brasil/hooks/use-dados-mae-pai'
import type { TCompradoresCadastrados } from '@/features/proposta/brasil/types/proposta.type'
import {
  StepPropostaContent,
  StepPropostaFooter,
  StepPropostaHeaderContent,
  StepPropostaHeaderTitle,
  StepPropostaRoot,
} from '@/features/proposta/shared/components/layouts/step-proposta'
import { useNacionalidadeCache } from '@/features/proposta/shared/hooks/use-nacionalidade-cache'
import type { TProfissao } from '@/types/cliente'
import { CPF_MASK } from '@/utils/masks'

type TDadosComprador = {
  onBack: () => void
  onNext: () => void
  compradores: TCompradoresCadastrados
  profissoes: TProfissao[]
}

export const DadosMaePai = ({ onNext, onBack, compradores, profissoes }: TDadosComprador) => {
  const {
    formState: { errors },
    control,
  } = useFormContext<CompradorPessoaFisicaStepSchemaInput>()
  const cpf = useWatch({ name: 'maePai.dados.cpf' })
  const cpfRaw = cpf?.replaceAll(/\D/g, '')
  const buscouCpf = useWatch({ name: 'maePai.dados.buscouCpf' })
  const isEditing = useWatch({ name: 'mode' }) === 'edit'

  const { nacionalidadePrincipalId, nacionalidades } = useNacionalidadeCache()

  const { refetch, isFetching } = useClientePorCpfProposta(String(cpfRaw), false)
  const { handlers } = useDadosMaePai({
    onNext,
    compradores,
    refetch,
    nacionalidadePrincipalId,
  })

  const prefix = 'proposta:cadastro_compradores.steps.dados_comprador'
  return (
    <StepPropostaRoot>
      <StepPropostaHeaderContent>
        <StepPropostaHeaderTitle>{t(`${prefix}.mae_pai_titulo`)}</StepPropostaHeaderTitle>
      </StepPropostaHeaderContent>
      <StepPropostaContent hasBorder>
        <div className="w-full">
          <h3 className="font-poppins neutral-800 mb-6 tracking-wide">{t(`${prefix}.subtitulo`)}</h3>
          <div className="mt-1 mb-4 grid max-w-114 grid-cols-[182px_1fr] items-end gap-4 md:grid-cols-[1fr_127px] lg:grid-cols-[362px_1fr] lg:gap-10">
            <InputField
              ref={withMask('cpf', CPF_MASK)}
              name="maePai.dados.cpf"
              control={control}
              type="text"
              label={t(`${prefix}.label.cpf`)}
              placeholder={t(`${prefix}.placeholder.cpf`)}
              disabled={buscouCpf || isEditing}
              afterContent={<Icon name="search" className="text-muted-foreground" />}
            />
            <Render.If condition={!isEditing}>
              {() => (
                <Render.If
                  condition={buscouCpf}
                  fallback={
                    <Button
                      isLoading={isFetching}
                      variant="outline"
                      loadingText=""
                      className="mb-7 border-gray-300 bg-white hover:bg-gray-50 sm:mb-9"
                      disabled={!cpfRaw || !!errors.maePai?.dados?.cpf}
                      onClick={handlers.handleBuscarCliente}
                    >
                      {t(`${prefix}.buscar`)}
                    </Button>
                  }
                >
                  {() => (
                    <Button
                      isLoading={isFetching}
                      variant="ghost"
                      className="text-destructive hover:text-destructive mb-7 border-gray-300 bg-white sm:mb-9"
                      onClick={handlers.handleLimpar}
                    >
                      <>
                        <span className="sm:hidden">{t(`${prefix}.limpar_mobile`)}</span>
                        <span className="hidden sm:inline">{t(`${prefix}.limpar`)}</span>
                      </>
                    </Button>
                  )}
                </Render.If>
              )}
            </Render.If>
          </div>
          <div className="mb-6 border-t border-neutral-200" />
          <div className="relative flex flex-1 flex-col gap-2 md:grid md:grid-cols-1 md:gap-0">
            <InputField
              className="max-w-90"
              disabled={!buscouCpf}
              required
              control={control}
              name="maePai.dados.nome"
              label={t(`${prefix}.label.nome`)}
              placeholder={t(`${prefix}.placeholder.nome`)}
              maxLength={100}
            />
            <ComboboxField
              disabled={!buscouCpf}
              className="max-w-90"
              placeholder={t(`${prefix}.placeholder.selecione`)}
              name="maePai.dados.nacionalidadeId"
              defaultValue={nacionalidades[0]?.id}
              control={control}
              label={t(`${prefix}.label.nacionalidade`)}
              options={nacionalidades}
              valueProperty="id"
              labelProperty="descricao"
              hideClearButton
            />
            <ComboboxField
              disabled={!buscouCpf}
              className="max-w-90"
              placeholder={t(`${prefix}.placeholder.selecione`)}
              name="maePai.dados.profissaoId"
              control={control}
              label={t(`${prefix}.label.profissao`)}
              options={profissoes}
              valueProperty="id"
              labelProperty="descricao"
              hideClearButton
            />
          </div>
        </div>
      </StepPropostaContent>
      <StepPropostaFooter>
        <Button variant="outline" onClick={onBack} className="font-nunito h-12 w-full md:w-auto">
          {t('proposta:cadastro_compradores.steps.voltar')}
        </Button>
        <Button className="font-nunito h-12 w-full md:w-auto" onClick={handlers.validateOnNext}>
          {t('proposta:cadastro_compradores.steps.proximo')}
        </Button>
      </StepPropostaFooter>
    </StepPropostaRoot>
  )
}
